import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RevenueAnalysis } from "./RevenueAnalysis";
import { PaymentTrends } from "./PaymentTrends";
import { ActivityProfitability } from "./ActivityProfitability";
import { useExportData } from "@/hooks/useExportData";
import { 
  BarChart3, TrendingUp, Activity, Download, Calendar,
  FileText, DollarSign, Users, Clock 
} from "lucide-react";

export const FinancialReports = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("current-month");
  const { exportReportData, isExporting } = useExportData();

  const handleExportReport = () => {
    const reportData = {
      period: selectedPeriod,
      type: "financial_report",
      totalRevenue: 3430,
      totalStudents: 69,
      avgOccupancy: 56.4,
      mostProfitable: "Tenis"
    };
    
    exportReportData(reportData, `reporte_financiero_${selectedPeriod}_${new Date().toISOString().split('T')[0]}`);
  };

  const getPeriodLabel = (period: string) => {
    switch (period) {
      case "current-month": return "Mes Actual";
      case "last-month": return "Mes Anterior";
      case "current-quarter": return "Trimestre Actual";
      case "last-quarter": return "Trimestre Anterior";
      case "current-year": return "Año Actual";
      case "last-year": return "Año Anterior";
      default: return "Período Seleccionado";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header con controles */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            Reportes Financieros
          </h2>
          <p className="text-muted-foreground mt-1">
            Análisis detallado de ingresos, pagos y tendencias
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-48">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current-month">Mes Actual</SelectItem>
              <SelectItem value="last-month">Mes Anterior</SelectItem>
              <SelectItem value="current-quarter">Trimestre Actual</SelectItem>
              <SelectItem value="last-quarter">Trimestre Anterior</SelectItem>
              <SelectItem value="current-year">Año Actual</SelectItem>
              <SelectItem value="last-year">Año Anterior</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            onClick={handleExportReport}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <Clock className="w-4 h-4 mr-2 animate-spin" />
                Exportando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Métricas de resumen del período */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Resumen - {getPeriodLabel(selectedPeriod)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-success">€2,450.00</div>
              <div className="text-sm text-muted-foreground">Ingresos Totales</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">€2,100.00</div>
              <div className="text-sm text-muted-foreground">Cobrado</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-warning">€350.00</div>
              <div className="text-sm text-muted-foreground">Pendiente</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-destructive">€125.00</div>
              <div className="text-sm text-muted-foreground">Vencido</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pestañas de reportes */}
      <Tabs defaultValue="revenue" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="revenue" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Ingresos
          </TabsTrigger>
          <TabsTrigger value="trends" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Tendencias
          </TabsTrigger>
          <TabsTrigger value="profitability" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Rentabilidad
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="revenue" className="mt-6">
          <RevenueAnalysis period={selectedPeriod} />
        </TabsContent>
        
        <TabsContent value="trends" className="mt-6">
          <PaymentTrends period={selectedPeriod} />
        </TabsContent>
        
        <TabsContent value="profitability" className="mt-6">
          <ActivityProfitability period={selectedPeriod} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancialReports;